import { useEffect, useId, useRef, useState } from 'react';
import { clearLandedDraft, useDraft } from '../../lib/useDraft';
import { draftKey } from '../../lib/drafts';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type TicketMessageVisibility } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { dayInTz, timeInTz } from '../../lib/format';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import { PressedGroup } from '../ui/PressedGroup';
import { refreshUntilShown } from '../ui/QueryStates';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from './sendOnce';
import {
  REPLY_ADDRESS_ANCHOR,
  deliveryLine,
  messageBadge,
  type DeliveryTone,
} from './ticketView';
import type { TicketDetail } from './types';

const DELIVERY_TONE: Record<DeliveryTone, string> = {
  done: 'text-muted-foreground',
  pending: 'text-muted-foreground',
  quiet: 'text-muted-foreground',
  problem: 'text-[var(--sw-warn-text)]',
};

/**
 * A ticket's conversation, oldest first: who, the day and time, and who it is for, and under
 * a staff message for the customer what became of its email (`deliveryLine`). The one
 * drawing of it, on the office's panel (`Messages`) and the tech's visit (`JobTicketsCard`),
 * which each bring their own box to add to it. `showDelivery` is the office's: a tech can do
 * nothing about an email (and their read carries none), so their card draws no line.
 */
export function MessageList({
  messages,
  tz,
  showDelivery,
}: {
  messages: TicketDetail['messages'];
  tz: string;
  showDelivery: boolean;
}) {
  return (
    <ol className="space-y-2">
      {messages.map((m) => (
        <li key={m.id} className="rounded-[var(--radius)] border border-border p-2">
          <p className="flex flex-wrap items-baseline justify-between gap-x-2 text-xs text-muted-foreground">
            <span>
              <span className="font-medium text-text">
                {m.author ? (m.author.name ?? 'Former staff') : 'The customer'}
              </span>{' '}
              · {dayInTz(m.createdAt, tz)} {timeInTz(m.createdAt, tz)}
            </span>
            <StatusBadge tone={m.visibility === 'customer' ? 'info' : 'neutral'}>
              {messageBadge(m)}
            </StatusBadge>
          </p>
          <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
          {showDelivery ? <Delivery message={m} /> : null}
        </li>
      ))}
    </ol>
  );
}

function Delivery({ message }: { message: TicketDetail['messages'][number] }) {
  const line = deliveryLine(message);
  if (!line) return null;
  return (
    <p data-delivery={line.tone} className={`mt-1 text-xs ${DELIVERY_TONE[line.tone]}`}>
      {line.text}
    </p>
  );
}

/**
 * The conversation, oldest first, and the box to add to it.
 *
 * ⭐ "For the customer" is offered only to whoever triages (`ticket:triage`: the owner and the
 * office), as the server allows (spec §4, `assertMayWriteToCustomer`). Anyone else's message
 * is internal, with no choice to make: a tech offered it would be refused online, and offline
 * would lose the words to a replay the server refuses (ticket 04 review, finding 1).
 */
export function Messages({
  detail,
  tz,
  canMessage,
}: {
  detail: TicketDetail;
  tz: string;
  canMessage: boolean;
}) {
  const ticketId = detail.ticket.id;
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const mayWriteToCustomer = useMayWriteToCustomer();
  // Kept on the device as it is typed (the tech's card's draft, by the same key): the hint's
  // link to Settings leaves this page, and what was typed is here on the way back.
  // The draft is NAMED in the write's meta (the tech card's pattern): a message queued offline
  // that lands after this panel let go of it (or after a restart) clears exactly this draft
  // (`clearLandedDraft`, ApiProvider's `onLanded`), so the sent words do not come back.
  const userId = useSession().userId;
  const [body, setBody] = useDraft({ kind: 'ticket-message', userId, id: ticketId }, '');
  const meta = { draftKey: draftKey('ticket-message', userId, ticketId) };
  const [visibility, setVisibility] = useState<TicketMessageVisibility>('internal');
  const boxId = useId();
  const hintId = useId();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const send = useMutation(
    trpc.ticket.message.mutationOptions({
      meta,
      onSuccess: async (_data, sent) => {
        await refreshUntilShown(qc, { queryKey: trpc.ticket.get.queryKey({ ticketId }) });
        // Only what was sent is cleared: words typed while it was on its way stay.
        if (mounted.current) {
          // On screen: the updater reads the newest text (`clearLandedDraft` reads the last
          // COMMITTED render, and would wipe a keystroke not yet drawn).
          setBody((now) => (now.trim() === sent.body ? '' : now));
        } else {
          // The panel closed while it was queued, and it landed in this session: its own
          // onSuccess runs (so `onLanded` does not), and a setter on an unmounted panel
          // reaches no disk. With no box on screen for this key, this clears only the
          // device's copy, and only while it still says what was sent.
          void clearLandedDraft('ticket.message', sent, meta);
        }
      },
      onSettled: () => lock.release(),
    }),
  );
  const empty = !body.trim();
  const toCustomer = mayWriteToCustomer && visibility === 'customer';
  return (
    <section aria-labelledby={`${ticketId}-messages`} className="space-y-3">
      <h3
        id={`${ticketId}-messages`}
        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Messages ({detail.messages.length})
      </h3>
      {detail.messages.length === 0 ? (
        <p className="text-muted-foreground">No messages yet.</p>
      ) : (
        <MessageList messages={detail.messages} tz={tz} showDelivery />
      )}
      {canMessage ? (
        <div className="space-y-2">
          {send.error ? (
            <Notice tone="danger" alert>
              {send.error.message}
            </Notice>
          ) : null}
          <label htmlFor={boxId} className="sr-only">
            New message
          </label>
          <textarea
            id={boxId}
            className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a message"
            aria-describedby={toCustomer ? hintId : undefined}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            {mayWriteToCustomer ? (
              <PressedGroup
                label="Who the message is for"
                options={[
                  { value: 'internal', label: 'Internal' },
                  { value: 'customer', label: 'For the customer' },
                ]}
                value={visibility}
                onChange={setVisibility}
              />
            ) : (
              <span className="text-xs text-muted-foreground">Internal</span>
            )}
            <Button
              aria-disabled={empty || send.isPending || undefined}
              onClick={() => {
                if (empty || send.isPending) return;
                sendOnce(lock, send.mutate, {
                  ticketId,
                  body: body.trim(),
                  visibility: toCustomer ? ('customer' as const) : ('internal' as const),
                });
              }}
            >
              {send.isPending ? 'Adding…' : 'Add message'}
            </Button>
          </div>
          {toCustomer ? <CustomerEmailHint id={hintId} linkToSettings /> : null}
        </div>
      ) : null}
    </section>
  );
}

/** Whether the person may write "for the customer": whoever triages (spec §4). */
export function useMayWriteToCustomer(): boolean {
  return can(useSession().role, 'ticket', 'triage');
}

/**
 * What a message "for the customer" will meet, under the choice (the composer's and New
 * ticket's): where the customer's replies go, or, with no CONFIRMED reply address yet, that the
 * message waits and is emailed once one is confirmed (MAP 249 spec §5: a ticket email waits
 * "Waiting for your reply address" and goes on its own; nothing is lost). Read from
 * `replyAddress.status` (ticket 04: the confirmed address, and the one waiting for its link),
 * never the typed `org.replyToEmail`; nothing while it loads (no hint beats a wrong one).
 *
 * Only the owner can set it, so only the owner is linked to the field (`linkToSettings`), and
 * only from a box that keeps its draft across the trip: New ticket's form is not kept, so it
 * names the place instead. Anyone else is told to ask the owner, with no link to a field they
 * cannot change.
 */
export function CustomerEmailHint({
  id,
  linkToSettings,
}: {
  id?: string;
  linkToSettings: boolean;
}) {
  const trpc = useTRPC();
  const me = useSession();
  const status = useQuery(trpc.replyAddress.status.queryOptions());
  if (!status.data) return null;
  const { confirmed, pending } = status.data;
  const until =
    ' Until then, a message for the customer waits, and is emailed once the address is confirmed.';
  return (
    <p id={id} data-customer-email-hint="" className="text-xs text-muted-foreground">
      {confirmed ? (
        <>
          Each message shows whether it was emailed. The customer&apos;s replies go to{' '}
          <span className="break-all font-medium text-text">{confirmed.email}</span>.
        </>
      ) : pending ? (
        <>
          Waiting for{' '}
          <span className="break-all font-medium text-text">{pending.email}</span> to be
          confirmed: a message for the customer waits, and is emailed once it is.
          {me.role === 'owner'
            ? ' Open the link ServWell emailed to that address.'
            : ' The owner confirms it from the link ServWell emailed to it.'}
        </>
      ) : me.role !== 'owner' ? (
        <>Ask the owner to add a reply address to email customers.{until}</>
      ) : linkToSettings ? (
        <>
          <Link
            to="/settings"
            hash={REPLY_ADDRESS_ANCHOR}
            className="font-medium text-[var(--sw-brand-text)] underline underline-offset-2"
          >
            Add a reply address in Settings
          </Link>{' '}
          to email customers.{until}
        </>
      ) : (
        <>Add a reply address in Settings (Email) to email customers.{until}</>
      )}
    </p>
  );
}
